import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface Session {
  session_id: string;
  status: string;
  started_at: string;
  cost_so_far: number;
}

interface Device {
  device_id: string;
  device_name: string;
  last_seen: string;
  created_at: string;
}

export default function DashboardPage() {
  const { user, session, signOut } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);

  useEffect(() => {
    loadSessions();
    loadDevices();
  }, []);

  const loadSessions = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://claude-companion-relay.kyliemckinleydemo.workers.dev';
      const response = await fetch(`${apiUrl}/api/sessions`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://claude-companion-relay.kyliemckinleydemo.workers.dev';
      const response = await fetch(`${apiUrl}/api/devices`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const handleNewSession = () => {
    setShowDeviceSelector(true);
  };

  const handleSelectDevice = async (deviceId: string) => {
    setCreatingSession(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://claude-companion-relay.kyliemckinleydemo.workers.dev';
      const response = await fetch(`${apiUrl}/api/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId,
          recording: true,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = `/session/${data.sessionId}`;
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    } finally {
      setCreatingSession(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Claude Code Companion</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button
              onClick={signOut}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Device Selector Modal */}
      {showDeviceSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Select a Device</h2>
              <button
                onClick={() => setShowDeviceSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {devices.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">No devices found.</p>
                  <Link
                    to="/pair"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Pair a device first
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <button
                      key={device.device_id}
                      onClick={() => handleSelectDevice(device.device_id)}
                      disabled={creatingSession}
                      className="w-full text-left p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{device.device_name}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            ID: {device.device_id.substring(0, 16)}...
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Last seen</p>
                          <p className="text-sm text-gray-700">
                            {new Date(device.last_seen).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {creatingSession && (
                <div className="mt-4 text-center text-gray-600">
                  Creating session...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick actions */}
        <div className="mb-8">
          <button
            onClick={handleNewSession}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium"
          >
            + New Session
          </button>
        </div>

        {/* Sessions list */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Sessions</h2>
          </div>
          <div className="divide-y">
            {loading ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Loading sessions...
              </div>
            ) : sessions.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No sessions yet. Create your first session to get started!
              </div>
            ) : (
              sessions.map((session) => (
                <Link
                  key={session.session_id}
                  to={`/session/${session.session_id}`}
                  className="block px-6 py-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{session.session_id.substring(0, 8)}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(session.started_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          session.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {session.status}
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        ${session.cost_so_far.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Quick links */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/pair"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition"
          >
            <h3 className="font-semibold mb-2">Pair Device</h3>
            <p className="text-sm text-gray-600">
              Connect a new computer to your account
            </p>
          </Link>
          <Link
            to="/billing"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition"
          >
            <h3 className="font-semibold mb-2">Billing</h3>
            <p className="text-sm text-gray-600">
              Manage subscription and view usage
            </p>
          </Link>
          <a
            href="https://docs.claudecompanion.com"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition"
            target="_blank"
            rel="noopener noreferrer"
          >
            <h3 className="font-semibold mb-2">Documentation</h3>
            <p className="text-sm text-gray-600">
              Learn how to get the most out of Companion
            </p>
          </a>
        </div>
      </main>
    </div>
  );
}
